import React, { useEffect, useMemo, useState } from 'react';
import { FaBolt, FaCheckCircle, FaPlus, FaRedo, FaRupeeSign, FaWallet } from 'react-icons/fa';
import api from '../lib/api';
import './CSS/PlatformFinance.css';

function readRole() {
  const raw = localStorage.getItem('role');
  if (!raw) return '';
  try {
    return String(JSON.parse(raw)).trim().toLowerCase();
  } catch {
    return String(raw).trim().toLowerCase();
  }
}

function formatCurrency(value) {
  return `Rs ${Number(value || 0).toFixed(2)}`;
}

function formatDate(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function PlatformBilling() {
  const token = localStorage.getItem('token');
  const role = readRole();
  const isSuperAdmin = role === 'super_admin';

  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [summary, setSummary] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    invoiceId: '',
    amount: '',
    payment_method: 'upi',
    payment_reference: '',
    collected_via: 'manual',
    remarks: '',
  });
  const [profileForm, setProfileForm] = useState({
    billing_amount: '',
    billing_cycle: 'monthly',
    billing_day: 1,
    grace_days: 3,
    next_invoice_date: new Date().toISOString().slice(0, 10),
    customer_upi_id: '',
    receiver_upi_id: '',
    autopay_enabled: false,
    autopay_mode: 'manual_collect',
    autopay_provider: '',
    autopay_provider_ref: '',
    auto_deactivate_enabled: true,
    notes: '',
  });

  useEffect(() => {
    if (isSuperAdmin) {
      fetchBillingHome();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (selectedTenantId) {
      fetchTenantDetail(selectedTenantId);
    }
  }, [selectedTenantId]);

  const fetchBillingHome = async () => {
    try {
      setLoading(true);
      const [summaryRes, tenantsRes] = await Promise.all([
        api.get('/api/platform-finance/billing/summary', authHeaders),
        api.get('/api/platform-finance/billing/tenants', authHeaders),
      ]);
      const tenantRows = tenantsRes.data || [];
      setSummary(summaryRes.data || null);
      setTenants(tenantRows);
      if (!selectedTenantId && tenantRows[0]?.id) {
        setSelectedTenantId(String(tenantRows[0].id));
      }
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to load billing workspace');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantDetail = async (tenantId) => {
    try {
      const { data } = await api.get(`/api/platform-finance/billing/tenants/${tenantId}`, authHeaders);
      setDetail(data || null);
      const tenant = data?.tenant || {};
      setProfileForm({
        billing_amount: tenant.billing_amount || '',
        billing_cycle: tenant.billing_cycle || 'monthly',
        billing_day: tenant.billing_day || 1,
        grace_days: tenant.grace_days || 3,
        next_invoice_date: tenant.next_invoice_date || new Date().toISOString().slice(0, 10),
        customer_upi_id: tenant.customer_upi_id || '',
        receiver_upi_id: tenant.receiver_upi_id || '',
        autopay_enabled: Number(tenant.autopay_enabled) === 1,
        autopay_mode: tenant.autopay_mode || 'manual_collect',
        autopay_provider: tenant.autopay_provider || '',
        autopay_provider_ref: tenant.autopay_provider_ref || '',
        auto_deactivate_enabled: Number(tenant.auto_deactivate_enabled) !== 0,
        notes: tenant.notes || '',
      });
      setPaymentForm((prev) => ({
        ...prev,
        invoiceId: data?.invoices?.[0]?.id ? String(data.invoices[0].id) : '',
      }));
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to load tenant billing detail');
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!selectedTenantId) return;
    try {
      setSaving(true);
      await api.put(`/api/platform-finance/billing/tenants/${selectedTenantId}/profile`, profileForm, authHeaders);
      await fetchBillingHome();
      await fetchTenantDetail(selectedTenantId);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to save billing profile');
    } finally {
      setSaving(false);
    }
  };

  const createInvoiceNow = async () => {
    if (!selectedTenantId) return;
    try {
      setSaving(true);
      await api.post(`/api/platform-finance/billing/tenants/${selectedTenantId}/invoices`, {}, authHeaders);
      await fetchBillingHome();
      await fetchTenantDetail(selectedTenantId);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to create invoice');
    } finally {
      setSaving(false);
    }
  };

  const recordPayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.invoiceId || !paymentForm.amount) return;
    try {
      setSaving(true);
      await api.post(
        `/api/platform-finance/billing/invoices/${paymentForm.invoiceId}/payments`,
        paymentForm,
        authHeaders
      );
      setPaymentForm((prev) => ({
        ...prev,
        amount: '',
        payment_reference: '',
        remarks: '',
      }));
      await fetchBillingHome();
      await fetchTenantDetail(selectedTenantId);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="platform-finance-page">
        <div className="platform-finance-surface">
          <h2>Super admin only</h2>
          <p>This billing control workspace is only available for the main platform owner account.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="platform-finance-page">
      <section className="platform-finance-hero">
        <div>
          <div className="platform-finance-chip">Tenant Billing Control</div>
          <h1>Collect subscriptions, mark dues, and auto-disable overdue tenants.</h1>
          <p>
            Configure per-tenant billing, collect through UPI-ready flows, capture payments in one place,
            and automatically deactivate stations once dues cross the allowed grace window.
          </p>
        </div>

        <button className="platform-finance-btn secondary" onClick={fetchBillingHome}>
          <FaRedo />
          <span>{loading ? 'Refreshing...' : 'Refresh'}</span>
        </button>
      </section>

      <section className="platform-finance-summary-grid">
        <StatCard label="Tenants" value={summary?.total_tenants || 0} hint={`${summary?.active_tenants || 0} currently active`} icon={<FaWallet />} />
        <StatCard label="Recurring Revenue" value={formatCurrency(summary?.recurring_revenue)} hint="Configured subscription value" icon={<FaRupeeSign />} />
        <StatCard label="Auto-pay Ready" value={summary?.autopay_enabled_count || 0} hint="Profiles marked for collect flow" icon={<FaBolt />} />
        <StatCard label="Overdue" value={summary?.overdue_tenants || 0} hint={`${formatCurrency(summary?.overdue_amount)} pending across tenants`} icon={<FaCheckCircle />} />
      </section>

      <section className="platform-finance-grid billing">
        <div className="platform-finance-surface">
          <div className="platform-finance-section-head">
            <div>
              <div className="platform-finance-kicker">Tenant List</div>
              <h3>Choose tenant billing account</h3>
            </div>
          </div>

          <div className="platform-billing-list">
            {tenants.map((tenant) => (
              <button
                key={tenant.id}
                className={`platform-billing-card ${String(selectedTenantId) === String(tenant.id) ? 'active' : ''}`}
                onClick={() => setSelectedTenantId(String(tenant.id))}
              >
                <div>
                  <strong>{tenant.name || '-'}</strong>
                  <span>{tenant.code || '-'} | {tenant.company || 'No company'}</span>
                </div>
                <div className="platform-billing-card-side">
                  <em>{formatCurrency(tenant.latest_balance_due || 0)}</em>
                  <small>{tenant.latest_invoice_status || 'No invoice'}</small>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="platform-finance-surface">
          <div className="platform-finance-section-head">
            <div>
              <div className="platform-finance-kicker">Billing Profile</div>
              <h3>{detail?.tenant?.name || 'Select tenant'}</h3>
            </div>
            <button className="platform-finance-btn primary" onClick={createInvoiceNow} disabled={saving || !selectedTenantId}>
              <FaPlus />
              <span>Create Invoice Now</span>
            </button>
          </div>

          <form className="platform-finance-form grid-2" onSubmit={saveProfile}>
            <input
              type="number"
              step="0.01"
              value={profileForm.billing_amount}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, billing_amount: e.target.value }))}
              placeholder="Billing amount"
            />
            <select
              value={profileForm.billing_cycle}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, billing_cycle: e.target.value }))}
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
            <input
              type="number"
              min="1"
              max="28"
              value={profileForm.billing_day}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, billing_day: e.target.value }))}
              placeholder="Billing day"
            />
            <input
              type="number"
              min="0"
              value={profileForm.grace_days}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, grace_days: e.target.value }))}
              placeholder="Grace days"
            />
            <input
              type="date"
              value={profileForm.next_invoice_date}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, next_invoice_date: e.target.value }))}
            />
            <input
              value={profileForm.customer_upi_id}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, customer_upi_id: e.target.value }))}
              placeholder="Customer UPI ID"
            />
            <input
              value={profileForm.receiver_upi_id}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, receiver_upi_id: e.target.value }))}
              placeholder="FuelFlow receive UPI ID"
            />
            <select
              value={profileForm.autopay_mode}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, autopay_mode: e.target.value }))}
            >
              <option value="manual_collect">Manual Collect / UPI Request</option>
              <option value="gateway_autopay">Gateway Auto-pay</option>
            </select>
            <input
              value={profileForm.autopay_provider}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, autopay_provider: e.target.value }))}
              placeholder="Gateway provider"
            />
            <input
              value={profileForm.autopay_provider_ref}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, autopay_provider_ref: e.target.value }))}
              placeholder="Mandate / provider ref"
            />
            <textarea
              value={profileForm.notes}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, notes: e.target.value }))}
              placeholder="Internal billing notes"
            />
            <label className="platform-finance-check">
              <input
                type="checkbox"
                checked={profileForm.autopay_enabled}
                onChange={(e) => setProfileForm((prev) => ({ ...prev, autopay_enabled: e.target.checked }))}
              />
              <span>Enable auto-pay flow for this tenant</span>
            </label>
            <label className="platform-finance-check">
              <input
                type="checkbox"
                checked={profileForm.auto_deactivate_enabled}
                onChange={(e) => setProfileForm((prev) => ({ ...prev, auto_deactivate_enabled: e.target.checked }))}
              />
              <span>Auto-deactivate after grace window</span>
            </label>
            <button className="platform-finance-btn primary full" type="submit" disabled={saving}>
              <FaCheckCircle />
              <span>Save Billing Profile</span>
            </button>
          </form>

          <div className="platform-finance-note">
            Live UPI auto-debit needs a gateway or mandate provider. This screen is wired to track UPI collect setup,
            payment posting, and overdue enforcement so the external provider can be plugged in cleanly.
          </div>
        </div>
      </section>

      <section className="platform-finance-grid two">
        <div className="platform-finance-surface">
          <div className="platform-finance-section-head">
            <div>
              <div className="platform-finance-kicker">Invoices</div>
              <h3>Recent billing invoices</h3>
            </div>
          </div>

          <div className="platform-finance-table-wrap">
            <table className="platform-finance-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Due</th>
                  <th>Amount</th>
                  <th>Balance</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {detail?.invoices?.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>{invoice.invoice_number}</td>
                    <td>{formatDate(invoice.due_date)}</td>
                    <td>{formatCurrency(invoice.amount_due)}</td>
                    <td>{formatCurrency(invoice.balance_due)}</td>
                    <td>{invoice.status}</td>
                  </tr>
                ))}
                {!detail?.invoices?.length && (
                  <tr>
                    <td colSpan="5">No invoices yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="platform-finance-surface">
          <div className="platform-finance-section-head">
            <div>
              <div className="platform-finance-kicker">Collections</div>
              <h3>Record payment against invoice</h3>
            </div>
          </div>

          <form className="platform-finance-form grid-2" onSubmit={recordPayment}>
            <select
              value={paymentForm.invoiceId}
              onChange={(e) => setPaymentForm((prev) => ({ ...prev, invoiceId: e.target.value }))}
            >
              <option value="">Select invoice</option>
              {(detail?.invoices || []).map((invoice) => (
                <option key={invoice.id} value={invoice.id}>
                  {invoice.invoice_number} | {formatCurrency(invoice.balance_due)}
                </option>
              ))}
            </select>
            <input
              type="number"
              step="0.01"
              value={paymentForm.amount}
              onChange={(e) => setPaymentForm((prev) => ({ ...prev, amount: e.target.value }))}
              placeholder="Amount received"
            />
            <select
              value={paymentForm.payment_method}
              onChange={(e) => setPaymentForm((prev) => ({ ...prev, payment_method: e.target.value }))}
            >
              <option value="upi">UPI</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="other">Other</option>
            </select>
            <select
              value={paymentForm.collected_via}
              onChange={(e) => setPaymentForm((prev) => ({ ...prev, collected_via: e.target.value }))}
            >
              <option value="manual">Manual Entry</option>
              <option value="autopay">Auto-pay</option>
              <option value="webhook">Gateway Webhook</option>
            </select>
            <input
              value={paymentForm.payment_reference}
              onChange={(e) => setPaymentForm((prev) => ({ ...prev, payment_reference: e.target.value }))}
              placeholder="Txn / collect reference"
            />
            <input
              value={paymentForm.remarks}
              onChange={(e) => setPaymentForm((prev) => ({ ...prev, remarks: e.target.value }))}
              placeholder="Remarks"
            />
            <button className="platform-finance-btn primary full" type="submit" disabled={saving}>
              <FaWallet />
              <span>Post Payment</span>
            </button>
          </form>

          <div className="platform-finance-table-wrap compact">
            <table className="platform-finance-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Via</th>
                </tr>
              </thead>
              <tbody>
                {detail?.payments?.map((payment) => (
                  <tr key={payment.id}>
                    <td>{formatDate(payment.payment_date)}</td>
                    <td>{formatCurrency(payment.amount)}</td>
                    <td>{payment.payment_method}</td>
                    <td>{payment.collected_via}</td>
                  </tr>
                ))}
                {!detail?.payments?.length && (
                  <tr>
                    <td colSpan="4">No payments posted yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}

function StatCard({ label, value, hint, icon }) {
  return (
    <div className="platform-finance-stat">
      <div className="platform-finance-stat-icon">{icon}</div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{hint}</small>
      </div>
    </div>
  );
}
